import { useEffect, useRef, useState } from 'react'

import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { IconXmarkLight } from '@smartbear/swagger-portal-component-library'
import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'

import { SearchProductsMobile } from './SearchProducts/mobile'
import { SearchResults } from './SearchResults'
import { SEARCH_URL } from '~/portal/components/SearchModal/constants'
import { buildSearchParams } from '~/portal/components/SearchModal/helpers'
import appDefaults from '~/portal/portal.defaults.json'
import { SearchLoaderData } from '~/portal/routes/services.api.search/_index'
import { LandingProduct } from '~/portal/types/product'
import { SEARCH_TAB_TYPE } from '~/portal/types/search'
import { addSearchParams } from '~/shared/utils/url'

interface SearchModalMobileProps {
  isOpen: boolean
  onClose: () => void
  accentColor?: string,
  products?: Array<LandingProduct>,
  currentProduct?: LandingProduct,
}

export const SearchModalMobile = ({ isOpen, onClose, accentColor, products = [], currentProduct }: SearchModalMobileProps) => {
  const fetcher = useFetcher<SearchLoaderData>()

  const { t } = useTranslation()

  const [searchData, setSearchData] = useState<SearchLoaderData>({
    results: [],
    noOfElements: {},
  })
  const [searchParams, setSearchParams] = useState({})
  const [inputLength, setInputLength] = useState(0)
  const [tab, setTab] = useState<SEARCH_TAB_TYPE>(SEARCH_TAB_TYPE.ALL)
  
  const isLoadingRef = useRef(false)
  
  const { minQueryLength, maxQueryLength } = appDefaults.search

  useEffect(() => {
    if (fetcher.data) {
      setSearchData(fetcher.data)
      isLoadingRef.current = false
    }
  }, [fetcher.data])

  const handleSearch = (query: string, type: SEARCH_TAB_TYPE, productId?: string) => {
    if (query?.trim().length < minQueryLength || query?.trim().length > maxQueryLength) {
      setSearchData({ results: [], noOfElements: {} })
    } else {
      isLoadingRef.current = true

      const searchParams = buildSearchParams(query, type, productId)
      setSearchParams(searchParams)
      fetcher.load(addSearchParams(SEARCH_URL, searchParams))
    }
  }

  const handleClear = () => {
    setSearchData({ results: [], noOfElements: {} })
    setSearchParams({})
    setInputLength(0)
  }

  return (
    // HeadlessUI always portals Dialog content to a global root outside this component's DOM,
    // so the sp-custom-css-scope/sp-app classes are added here to travel with the portaled subtree
    <Dialog className="sp-custom-css-scope sp-app fixed inset-0 z-50" onClose={onClose} open={isOpen}>
      <div className="flex items-center justify-center w-screen h-screen">
        <DialogPanel className="sp-search-modal w-full h-full bg-(--sp-color-surface)">
          <div className="flex items-center justify-between p-6 border-b border-(--sp-color-border-subtle)">
            <DialogTitle className="font-medium text-(--sp-color-text) text-h3">
              {t('components.ui.searchModal.mobileTitle')}
            </DialogTitle>
            <button onClick={onClose} data-testid="close-button" className="sp-search-modal-close p-[0.375rem] text-(--sp-color-text-muted)">
              <IconXmarkLight height={20} width={20} />
            </button> 
          </div>
          <div className="p-6 md:px-9" >
            <SearchProductsMobile
              onSearch={handleSearch}
              accentColor={accentColor}
              isLoading={fetcher.state === 'loading'}
              onInputLengthChange={setInputLength}
              products={products}
              currentProduct={currentProduct}
              allProductsLabel={t('components.ui.searchModal.allProducts')}
              tab={tab}
              handleClear={handleClear}
            />
            <SearchResults
              noOfElements={searchData.noOfElements}
              results={searchData.results || []}
              isLoading={isLoadingRef.current}
              inputLength={inputLength}
              minQueryLength={minQueryLength}
              onClose={onClose}
              onTabChange={setTab}
              isMobile
              searchParams={searchParams}
              totalElements={searchData.searchPage?.totalElements || 0}
              selectedTab={tab}
            />
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}